import { combineAngle, isAnglePart } from "./angle";

type AnglePart = "Degrees" | "Minutes";
interface AngleValidation {
	value: number | null;
	message?: string;
	invalidParts?: AnglePart[];
}
const UPPER_BOUND_MESSAGE = "Angle must not exceed 360°";

/**
 * Most minutes give a repeating decimal (20′ = 0.3333…). Limits and readings are
 * stored as DECIMAL(9,4), so round the same way the spec editor does. Otherwise
 * an angle exactly on a limit compares as Over/Under before it is saved.
 */
const toStoredAngle = (value: number): number => Number(value.toFixed(4));

/** Converted minutes can repeat: validate bounds, not scalar decimal precision. */
export const validateAngleValue = (value: number): string | null => {
	if (!Number.isFinite(value)) return "Enter a finite angle";
	if (value < 0) return "Angle must not be negative";
	return value > 360 ? UPPER_BOUND_MESSAGE : null;
};

/** Shared entry rules; signed-angle decoding/conversion stays unchanged. */
export const validateAngle = (
	degrees: string,
	minutes: string,
): AngleValidation => {
	const errors: { part: AnglePart; message: string }[] = [];
	if (!isAnglePart(degrees))
		errors.push({ part: "Degrees", message: "Degrees must be a whole number" });
	else {
		const message = validateAngleValue(Number(degrees));
		if (message) errors.push({ part: "Degrees", message });
	}
	if (!isAnglePart(minutes))
		errors.push({ part: "Minutes", message: "Minutes must be a whole number" });
	else if (Number(minutes) < 0 || Number(minutes) > 59)
		errors.push({
			part: "Minutes",
			message: "Minutes must be between 0 and 59",
		});
	if (errors.length)
		return {
			value: null,
			message: errors.map(({ message }) => message).join("; "),
			invalidParts: errors.map(({ part }) => part),
		};
	const combined = combineAngle(degrees, minutes);
	const value = combined === null ? null : toStoredAngle(combined);
	if (value !== null && value > 360) {
		return {
			value: null,
			message: UPPER_BOUND_MESSAGE,
			invalidParts: ["Degrees", "Minutes"],
		};
	}
	return { value };
};
